import React, { useState, useEffect } from 'react';
import { Container, ProgressBar, Button, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { getSeekAndFindImages, getWordById } from '../../../services/gameService';
import { getProblemSounds } from '../../../services/pronunciationTestService';
import { auth } from '../../../firebase';
import Icon from '../../UI/Icon';
import PhoneUnavailableMessage from '../../UI/PhoneUnavailableMessage';
import { LuGamepad2 } from "react-icons/lu";
import { IoVolumeMuteOutline } from "react-icons/io5";
import sprechheldenHeldFlying from '../../../assets/sprechheldenHeld_flying.png';
import gameWon from '../../../assets/gameWon.png';
import helpLogo from '../../../assets/logo_logo_sh.png';
import background1 from '../../../assets/background_1.png';
import Confetti from 'react-confetti';
import { isPhone as checkIsPhone, isTabletPortrait, setupDeviceDetection } from '../../../utils/deviceDetection';

function SuchbilderGame({ difficulty = 'medium', onBackToGames }) {
  const [feedback, setFeedback] = useState(null);
  const [foundObjects, setFoundObjects] = useState(new Set());
  const [foundObjectNames, setFoundObjectNames] = useState(new Set());
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [gamePhase, setGamePhase] = useState('selection'); // 'selection', 'playing'
  const [availablePictures, setAvailablePictures] = useState([]);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [helpUsed, setHelpUsed] = useState(false);
  const [foundObjectPositions, setFoundObjectPositions] = useState([]);
  const [gameMode, setGameMode] = useState('single'); // 'single' or 'two-player'
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [playerScores, setPlayerScores] = useState({ player1: 0, player2: 0 });
  const [showGameModeModal, setShowGameModeModal] = useState(false);
  const [glowingObject, setGlowingObject] = useState(null);
  const [foundWords, setFoundWords] = useState([]);
  const [showWordReview, setShowWordReview] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedLauts, setSelectedLauts] = useState([]);
  const [availableLauts, setAvailableLauts] = useState([]);
  const [picturesLoading, setPicturesLoading] = useState(false);

  // Fetch seek and find images data from database
  useEffect(() => {
    const fetchSeekAndFindData = async () => {
      try {
        // Use picturesLoading for subsequent loads, loading for initial load
        const isInitialLoad = availablePictures.length === 0;
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setPicturesLoading(true);
        }
        
        const data = await getSeekAndFindImages();
        
        // Map English difficulty levels to German database terms
        const difficultyMapping = {
          'easy': 'Leicht',
          'medium': 'Mittel', 
          'hard': 'Schwer'
        };
        
        const germanDifficulty = difficultyMapping[difficulty];
        
        // Filter pictures that have hiddenObjects and match the selected difficulty
        let picturesWithObjects = data.filter(picture => 
          picture.hiddenObjects && picture.hiddenObjects.length > 0 &&
          picture.difficulty === germanDifficulty
        );

        // Further filter by selected sounds if any sounds are selected
        if (selectedLauts.length > 0) {
          picturesWithObjects = picturesWithObjects.filter(picture => {
            if (picture.laut) {
              // Handle both string and array cases
              if (Array.isArray(picture.laut)) {
                return picture.laut.some(laut => selectedLauts.includes(laut));
              } else if (typeof picture.laut === 'string') {
                return selectedLauts.includes(picture.laut);
              }
            }
            return false;
          });
        }
        
        // Add minimum delay for smooth transition when filtering
        if (!isInitialLoad) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        setAvailablePictures(picturesWithObjects);
      } catch (error) {
        console.error('Error fetching seek and find images:', error);
      } finally {
        setLoading(false);
        setPicturesLoading(false);
      }
    };
    
    fetchSeekAndFindData();
  }, [difficulty, selectedLauts]);

  // Fetch lauts from wimmelbild_images collection filtered by difficulty
  useEffect(() => {
    const fetchLauts = async () => {
      try {
        // Get sounds from seek and find images (wimmelbild_images collection)
        const seekAndFindData = await getSeekAndFindImages();
        
        // Map English difficulty levels to German database terms
        const difficultyMapping = {
          'easy': 'Leicht',
          'medium': 'Mittel', 
          'hard': 'Schwer'
        };
        
        const germanDifficulty = difficultyMapping[difficulty];
        
        // Filter by difficulty first, then extract lauts
        const picturesLauts = seekAndFindData
          .filter(picture => picture.difficulty === germanDifficulty && picture.laut)
          .flatMap(picture => {
            if (Array.isArray(picture.laut)) {
              return picture.laut;
            } else if (typeof picture.laut === 'string') {
              return [picture.laut];
            }
            return [];
          });

        const lauts = picturesLauts
          .map(laut => laut && typeof laut === 'string' ? laut.trim() : laut)
          .filter(laut => laut && typeof laut === 'string' && laut !== '')
          .filter((laut, index, array) => array.indexOf(laut) === index)
          .sort();
        
        setAvailableLauts(lauts);

        // Auto-select user's severe problem sounds
        const user = auth.currentUser;
        if (user) {
          // Use getProblemSounds service to get sounds from pronunciation tests collection
          const problemSoundsData = await getProblemSounds(user.uid);
          const severeSounds = problemSoundsData.severeProblemSounds;

          if (severeSounds && Array.isArray(severeSounds) && severeSounds.length > 0) {
            // Pre-select severe problem sounds that are available in the game (case-insensitive)
            const soundsToPreselect = severeSounds.filter(sound =>
              lauts.some(laut => laut.toLowerCase() === sound.toLowerCase())
            ).map(sound => {
              // Return the actual laut from available lauts (with correct casing)
              return lauts.find(laut => laut.toLowerCase() === sound.toLowerCase());
            }).filter(Boolean);

            // Limit to max 2 sounds for SuchbilderGame
            const limitedSounds = soundsToPreselect.slice(0, 2);
            setSelectedLauts(limitedSounds);
          } else {
            // Clear selected lauts that are no longer available for this difficulty
            setSelectedLauts(prev => prev.filter(laut => lauts.includes(laut)));
          }
        } else {
          // Clear selected lauts that are no longer available for this difficulty
          setSelectedLauts(prev => prev.filter(laut => lauts.includes(laut)));
        }
      } catch (error) {
        console.error('Error fetching lauts from wimmelbild_images:', error);
      }
    };
    
    fetchLauts();
  }, [difficulty]);

  // Phone and tablet portrait detection on component mount
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      setIsPhone(checkIsPhone());
      setIsTabletPortraitMode(isTabletPortrait());
    });
    return cleanup;
  }, []);

  // Track window width for responsive image sizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Function to convert nominative to accusative case
  const toAccusative = (nominativeArticle) => {
    switch (nominativeArticle) {
      case 'der':
        return 'den';
      case 'die':
        return 'die';
      case 'das':
        return 'das';
      default:
        return nominativeArticle; // fallback
    }
  };

  // Function to play audio
  const playAudio = (audioUrl) => {
    if (audioUrl && !isMuted) {
      try {
        const audio = new Audio(audioUrl);
        audio.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      } catch (error) {
        console.error('Error creating audio object:', error);
      }
    }
  };

  // Function to toggle mute state
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Function to show help - reveals current object and plays audio
  const showHelp = () => {
    if (currentObject && unfoundObjects.length > 0) {
      setHelpUsed(true);
      setFeedback({ 
        type: 'info', 
        message: `Hilfe: Suche nach ${toAccusative(currentObject.article)} ${currentObject.name}!` 
      });
      playAudio(currentObject.audio);
      
      // Set the glowing object
      const currentObjectIndex = selectedObjects.findIndex(obj => obj === currentObject);
      if (currentObjectIndex !== -1) {
        setGlowingObject(currentObjectIndex);
      }
      
      setTimeout(() => {
        setFeedback(null);
        setHelpUsed(false);
        setGlowingObject(null);
      }, 3000);
    }
  };

  // Function to convert database object format to game format
  const convertDatabaseObject = async (dbObject) => {
    try {
      // Fetch word data using wordRef
      const wordData = await getWordById(dbObject.wordRef);

      if (!wordData) {
        console.error('Word not found for wordRef:', dbObject.wordRef);
        return null;
      }

      return {
        name: wordData.word,
        article: wordData.article || 'das', // use word article or default to 'das'
        audio: wordData.audio || null, // include audio field
        image: wordData.linkedImage || wordData.LinkedImage || null, // include image field
        area: {
          minX: dbObject.x,
          maxX: dbObject.x + dbObject.width,
          minY: dbObject.y,
          maxY: dbObject.y + dbObject.height
        },
        id: dbObject.id,
        color: dbObject.color,
        wordRef: dbObject.wordRef
      };
    } catch (error) {
      console.error('Error converting database object:', error);
      return null;
    }
  };

  // Function to load all objects from selected picture (making them all clickable)
  const loadAllObjects = async (pictureData) => {
    if (!pictureData || !pictureData.hiddenObjects || pictureData.hiddenObjects.length === 0) {
      return [];
    }
    
    // Convert all objects asynchronously (no more random selection)
    const convertedObjects = await Promise.all(
      pictureData.hiddenObjects.map(convertDatabaseObject)
    );
    
    // Filter out any null results (failed conversions)
    return convertedObjects.filter(obj => obj !== null);
  };

  // Function to select game mode
  const selectGameMode = (mode) => {
    setGameMode(mode);
  };

  // Handle sound selection
  const handleLautSelection = (laut) => {
    setSelectedLauts(prev => {
      if (prev.includes(laut)) {
        return prev.filter(l => l !== laut);
      } else {
        if (prev.length < 2) {
          return [...prev, laut];
        }
        return prev;
      }
    });
  };

  // Handle picture selection - show game mode modal first
  const handlePictureSelect = (picture) => {
    setSelectedPicture(picture);
    setShowGameModeModal(true);
  };

  // Start game after mode selection
  const startGame = async () => {
    if (selectedPicture) {
      const gameObjects = await loadAllObjects(selectedPicture);
      setSelectedObjects(gameObjects);
      setGamePhase('playing');
      setFoundObjects(new Set());
      setFoundObjectNames(new Set());
      setCurrentTargetIndex(0);
      setGameComplete(false);
      setFeedback(null);
      setHelpUsed(false);
      setFoundObjectPositions([]);
      setCurrentPlayer(1);
      setPlayerScores({ player1: 0, player2: 0 });
      setShowGameModeModal(false);
      setGlowingObject(null);
      setFoundWords([]);
      setShowWordReview(false);
      setShowConfetti(false);
    }
  };

  // Get unfound objects and select current target
  const unfoundObjects = selectedObjects.filter((obj, index) => !foundObjects.has(index));
  const currentObject = unfoundObjects.length > 0 ? unfoundObjects[currentTargetIndex % unfoundObjects.length] : null;

  const handleClick = (event) => {
    if (gameComplete || unfoundObjects.length === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const xPct = (event.clientX - rect.left) / rect.width;
    const yPct = (event.clientY - rect.top) / rect.height;
    
    // Check if click hits any unfound object
    let foundObjectIndex = -1;
    let foundObject = null;

    for (let i = 0; i < selectedObjects.length; i++) {
      if (foundObjects.has(i)) continue; // Skip already found objects

      const obj = selectedObjects[i];

      // Skip objects with names that have already been found
      if (foundObjectNames.has(obj.name)) continue;

      const hit =
        xPct >= obj.area.minX && xPct <= obj.area.maxX &&
        yPct >= obj.area.minY && yPct <= obj.area.maxY;

      if (hit) {
        foundObjectIndex = i;
        foundObject = obj;
        break;
      }
    }

    if (foundObject) {
      const newFoundObjects = new Set(foundObjects);
      newFoundObjects.add(foundObjectIndex);
      setFoundObjects(newFoundObjects);

      // Track the object name to prevent selecting the same object name again
      const newFoundObjectNames = new Set(foundObjectNames);
      newFoundObjectNames.add(foundObject.name);
      setFoundObjectNames(newFoundObjectNames);

      // Add found word to foundWords array for review
      const foundWordData = {
        name: foundObject.name,
        article: foundObject.article,
        audio: foundObject.audio,
        image: foundObject.image
      };
      setFoundWords(prev => [...prev, foundWordData]);

      // Store the center position of the found object's area for the red X mark
      const centerX = (foundObject.area.minX + foundObject.area.maxX) / 2;
      const centerY = (foundObject.area.minY + foundObject.area.maxY) / 2;
      const newPositions = [...foundObjectPositions, {
        x: centerX * 100, // convert to percentage
        y: centerY * 100,
        objectIndex: foundObjectIndex
      }];
      setFoundObjectPositions(newPositions);

      // Play audio for the found object
      playAudio(foundObject.audio);

      // Update player score in two-player mode
      if (gameMode === 'two-player') {
        const currentPlayerKey = currentPlayer === 1 ? 'player1' : 'player2';
        setPlayerScores(prev => ({
          ...prev,
          [currentPlayerKey]: prev[currentPlayerKey] + 1
        }));
      }

      if (newFoundObjects.size === 5) {
        // Game complete - show word review modal
        if (gameMode === 'two-player') {
          // Calculate final scores including the current point
          const currentPlayerKey = currentPlayer === 1 ? 'player1' : 'player2';
          const updatedScores = {
            ...playerScores,
            [currentPlayerKey]: playerScores[currentPlayerKey] + 1
          };
          const winner = updatedScores.player1 > updatedScores.player2 ? 'Spieler 1' : 
                        updatedScores.player2 > updatedScores.player1 ? 'Spieler 2' : 'Unentschieden';
          setFeedback({ type: 'success', message: `Spiel beendet! ${winner === 'Unentschieden' ? 'Unentschieden!' : winner + ' gewinnt!'}` });
        } else {
          setFeedback({ type: 'success', message: 'Glückwunsch! Du hast 5 Objekte gefunden!' });
        }
        setGameComplete(true);
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
        }, 5000); // Stop confetti after 5 seconds
        setTimeout(() => {
          setShowWordReview(true);
        }, 2000); // Show review after celebration animation
      } else {
        // Object found, continue game (player keeps turn in two-player mode)
        setFeedback({ type: 'success', message: `Super! Du hast ${toAccusative(foundObject.article)} ${foundObject.name} gefunden!` });
        setTimeout(() => {
          setCurrentTargetIndex(Math.floor(Math.random() * (selectedObjects.length - newFoundObjects.size)));
          setFeedback(null);
        }, 500);
      }
    } else {
      // Wrong click - switch player in two-player mode
      if (gameMode === 'two-player') {
        setFeedback({ type: 'error', message: `Spieler ${currentPlayer}: Das ist kein verstecktes Objekt! Spieler ${currentPlayer === 1 ? 2 : 1} ist dran.` });
        setTimeout(() => {
          setCurrentPlayer(prevPlayer => prevPlayer === 1 ? 2 : 1);
          setFeedback(null);
        }, 2000);
      } else {
        setFeedback({ type: 'error', message: `Das ist kein verstecktes Objekt, versuche es nochmal!` });
        setTimeout(() => {
          setFeedback(null);
        }, 2000);
      }
    }
  };

  const resetGame = async () => {
    if (selectedPicture) {
      const gameObjects = await loadAllObjects(selectedPicture);
      setSelectedObjects(gameObjects);
      setFoundObjects(new Set());
      setFoundObjectNames(new Set());
      setCurrentTargetIndex(0);
      setGameComplete(false);
      setFeedback(null);
      setHelpUsed(false);
      setFoundObjectPositions([]);
      setCurrentPlayer(1);
      setPlayerScores({ player1: 0, player2: 0 });
      setGlowingObject(null);
      setFoundWords([]);
      setShowWordReview(false);
      setShowConfetti(false);
    }
  };

  const goBackToSelection = () => {
    setGamePhase('selection');
    setSelectedPicture(null);
    setSelectedObjects([]);
    setFoundObjects(new Set());
    setFoundObjectNames(new Set());
    setCurrentTargetIndex(0);
    setGameComplete(false);
    setFeedback(null);
    setHelpUsed(false);
    setFoundObjectPositions([]);
    setGameMode('single');
    setCurrentPlayer(1);
    setPlayerScores({ player1: 0, player2: 0 });
    setShowGameModeModal(false);
    setGlowingObject(null);
    setFoundWords([]);
    setShowWordReview(false);
    setShowConfetti(false);
  };

  const playWithDifferentPicture = async () => {
    // Filter out the current picture to get other available pictures
    const otherPictures = availablePictures.filter(pic => pic.id !== selectedPicture?.id);

    if (otherPictures.length === 0) {
      // No other pictures available, just restart current one
      setShowWordReview(false);
      resetGame();
      return;
    }

    // Randomly select a different picture
    const randomIndex = Math.floor(Math.random() * otherPictures.length);
    const newPicture = otherPictures[randomIndex];

    // Load objects from the new picture and start the game
    const gameObjects = await loadAllObjects(newPicture);
    setSelectedPicture(newPicture);
    setSelectedObjects(gameObjects);
    setShowWordReview(false);
    setFoundObjects(new Set());
    setFoundObjectNames(new Set());
    setCurrentTargetIndex(0);
    setGameComplete(false);
    setFeedback(null);
    setHelpUsed(false);
    setFoundObjectPositions([]);
    setCurrentPlayer(1);
    setPlayerScores({ player1: 0, player2: 0 });
    setGlowingObject(null);
    setFoundWords([]);
    setShowConfetti(false);
  };

  // Show phone warning if on phone
  if (isPhone) {
    return <PhoneUnavailableMessage gameTitle="Sprechhelden auf Lautsuche" onBackToGames={onBackToGames} />;
  }

  if (loading) {
    return (
      <Container fluid>
        {/* Sound Selection Section Skeleton */}
        <div className="mb-4" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="text-center mb-3">
            <h2 className="mb-0">Laute auswählen</h2>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {/* Games Menu Button */}
            {onBackToGames && (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
              >
                <Button 
                  onClick={onBackToGames}
                  variant="secondary"
                  size="lg"
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                    flexShrink: 0
                  }}
                >
                  <LuGamepad2 size={24} />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Lauts Grid Skeleton */}
            <div 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                gap: '10px',
                justifyContent: 'center',
                border: '2px solid #dee2e6',
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                flex: 1
              }}
            >
              {[...Array(8)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    backgroundColor: '#e9ecef',
                    height: '40px',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Skeleton Cards for Pictures */}
        <div className="row justify-content-center">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="col-md-6 col-lg-4 mb-4">
              <div className="card h-100">
                <div
                  style={{
                    height: '300px',
                    backgroundColor: '#e9ecef',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.2}s`
                  }}
                />
                <div className="card-body">
                  <div
                    style={{
                      height: '20px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      width: '70%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.2}s`
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
          `}
        </style>
      </Container>
    );
  }

  if (gamePhase === 'selection') {
    return (
      <Container fluid>
        {/* Sound Selection Section */}
        <div className="mb-4" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="text-center mb-3">
            <h2 className="mb-0">Laute auswählen</h2>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {/* Games Menu Button */}
            {onBackToGames && (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
              >
                <Button 
                  onClick={onBackToGames}
                  variant="secondary"
                  size="lg"
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                    flexShrink: 0
                  }}
                >
                  <LuGamepad2 size={24} />
                </Button>
              </OverlayTrigger>
            )}
            
            {/* Lauts Grid */}
            <div 
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                gap: '10px',
                justifyContent: 'center',
                border: '2px solid #dee2e6',
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                flex: 1
              }}
            >
            {availableLauts.map((laut) => (
              <button
                key={laut}
                onClick={() => handleLautSelection(laut)}
                className="btn"
                style={{
                  padding: '10px',
                  borderRadius: '8px',
                  border: selectedLauts.includes(laut) ? '2px solid #28a745' : '2px solid #dee2e6',
                  backgroundColor: selectedLauts.includes(laut) ? '#d4edda' : '#ffffff',
                  color: selectedLauts.includes(laut) ? '#155724' : '#495057',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  cursor: selectedLauts.length >= 2 && !selectedLauts.includes(laut) ? 'not-allowed' : 'pointer',
                  opacity: selectedLauts.length >= 2 && !selectedLauts.includes(laut) ? 0.5 : 1
                }}
                disabled={selectedLauts.length >= 2 && !selectedLauts.includes(laut)}
              >
                {laut}
              </button>
            ))}
            </div>
          </div>
          
        </div>

        {picturesLoading ? (
          /* Skeleton Cards while filtering */
          <div className="row justify-content-center">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100">
                  <div
                    style={{
                      height: '300px',
                      backgroundColor: '#e9ecef',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.2}s`
                    }}
                  />
                  <div className="card-body">
                    <div
                      style={{
                        height: '20px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        width: '70%',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${index * 0.2}s`
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : availablePictures.length === 0 ? (
          <div className="text-center">
            <p>Keine Sprechhelden auf Lautsuche verfügbar.</p>
          </div>
        ) : (
          <div className="row justify-content-center">
            {availablePictures.map((picture, index) => (
              <div
                key={picture.id}
                className="col-md-6 col-lg-4 mb-4"
                style={{
                  animation: 'fadeIn 0.5s ease-in',
                  animationDelay: `${index * 0.1}s`,
                  animationFillMode: 'both'
                }}
              >
                <div
                  className="card h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handlePictureSelect(picture)}
                >
                  <div style={{ position: 'relative', minHeight: '200px', overflow: 'hidden', borderRadius: '0.375rem 0.375rem 0 0' }}>
                    {/* Shimmer placeholder */}
                    <div
                      className="thumbnail-shimmer"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.4s ease-in-out infinite'
                      }}
                    />
                    <img
                      src={picture.imageUrl}
                      alt={picture.name}
                      loading="lazy"
                      className="card-img-top"
                      style={{
                        maxHeight: '300px',
                        width: '100%',
                        objectFit: 'contain',
                        position: 'relative',
                        zIndex: 1,
                        opacity: 0,
                        transition: 'opacity 0.4s ease-in'
                      }}
                      onLoad={(e) => {
                        e.target.style.opacity = '1';
                        const shimmer = e.target.parentElement.querySelector('.thumbnail-shimmer');
                        if (shimmer) shimmer.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="card-body">
                    <p className="card-text">
                      {picture.hiddenObjects.length} versteckte Objekte
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }

            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>

        {/* Game Mode Selection Modal - Available during selection phase */}
        <Modal
          show={showGameModeModal}
          onHide={() => setShowGameModeModal(false)}
          backdrop="static"
          keyboard={false}
          size="lg"
          centered
        >
          <Modal.Header style={{
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.375rem 0.375rem 0 0',
            position: 'relative'
          }}>
            <Modal.Title style={{
              fontWeight: 'bold',
              color: '#4A4A4A'
            }}>
              Spielmodus wählen
            </Modal.Title>
            <button
              onClick={() => setShowGameModeModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#6c757d',
                cursor: 'pointer',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
            >
              ×
            </button>
          </Modal.Header>
          <Modal.Body style={{ padding: '2rem' }}>
            <p className="text-center mb-4" style={{ 
              fontSize: '16px',
              color: '#7A7A7A',
              marginBottom: '1.5rem'
            }}>
              Wähle den Spielmodus:
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              marginBottom: '2rem'
            }}>
              <Button
                onClick={() => selectGameMode('single')}
                style={{
                  minWidth: '150px',
                  minHeight: '60px',
                  borderRadius: '15px',
                  backgroundColor: gameMode === 'single' ? '#88D8B0' : '#f8f9fa',
                  border: '2px solid',
                  borderColor: gameMode === 'single' ? '#88D8B0' : '#dee2e6',
                  color: gameMode === 'single' ? '#4A4A4A' : '#7A7A7A',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  boxShadow: gameMode === 'single' 
                    ? '0 4px 12px rgba(136, 216, 176, 0.3)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                Einzelspieler
              </Button>
              <Button
                onClick={() => selectGameMode('two-player')}
                style={{
                  minWidth: '150px',
                  minHeight: '60px',
                  borderRadius: '15px',
                  backgroundColor: gameMode === 'two-player' ? '#FF6B6B' : '#f8f9fa',
                  border: '2px solid',
                  borderColor: gameMode === 'two-player' ? '#FF6B6B' : '#dee2e6',
                  color: gameMode === 'two-player' ? 'white' : '#7A7A7A',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  boxShadow: gameMode === 'two-player' 
                    ? '0 4px 12px rgba(255, 107, 107, 0.3)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                Zwei Spieler
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ 
            borderTop: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            borderRadius: '0 0 0.375rem 0.375rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Button 
              onClick={startGame}
              style={{
                minWidth: '150px',
                minHeight: '60px',
                borderRadius: '30px',
                backgroundColor: '#88D8B0',
                border: '2px solid #88D8B0',
                color: '#4A4A4A',
                fontWeight: 'bold',
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(136, 216, 176, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              Spiel starten
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeInOverlay {
            0% {
              opacity: 0;
              backdrop-filter: blur(0px);
            }
            100% {
              opacity: 1;
              backdrop-filter: blur(1px);
            }
          }
          
          @keyframes bounceCheckmark {
            0% {
              transform: scale(0) rotate(0deg);
              opacity: 0;
            }
            50% {
              transform: scale(1.3) rotate(180deg);
            }
            100% {
              transform: scale(1) rotate(360deg);
              opacity: 1;
            }
          }
          
          @keyframes bounceCheckMark {
            0% {
              transform: translate(-50%, -50%) scale(0);
              opacity: 0;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.2);
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.5;
            }
          }
          
          .check-mark {
            position: absolute;
            color: green;
            font-size: 60px;
            font-weight: bold;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            transform: translate(-50%, -50%);
            z-index: 10;
            animation: bounceCheckMark 0.5s ease-out;
            user-select: none;
          }
          
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(255, 107, 107, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
            }
          }
          
          @keyframes pulseCyan {
            0% {
              box-shadow: 0 0 0 0 rgba(78, 205, 196, 0.7);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(78, 205, 196, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(78, 205, 196, 0);
            }
          }
          
          @keyframes objectGlow {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 255, 0, 0.8);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(255, 255, 0, 0.3);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 255, 0, 0);
            }
          }
          
          .object-glow {
            position: absolute;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(255, 255, 0, 0.3) 0%, rgba(255, 255, 0, 0) 70%);
            animation: objectGlow 1s ease-out infinite;
            pointer-events: none;
            z-index: 5;
          }
          
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
            100% {
              transform: translateY(0px);
            }
          }

          /* iPad Portrait - Optimize image sizing and layout */
          @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
            .suchbilder-game-container {
              padding: 12px !important;
            }

            /* Override inline styles for game image container */
            [style*="position: relative"][style*="display: inline-block"] {
              max-width: 75% !important;
            }

            /* Control buttons sizing */
            .btn-lg {
              min-width: 55px !important;
              min-height: 55px !important;
            }

            /* Progress bar container */
            [style*="maxWidth"][style*="padding: 0 20px 5px"] {
              max-width: 80% !important;
            }

            /* Word review modal cards */
            [style*="width: 140px"][style*="height: 140px"] {
              width: 110px !important;
              height: 110px !important;
            }
          }
        `}
      </style>
      {!isPhone && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: '100vh',
          backgroundImage: `url(${background1})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: 0.2,
          zIndex: 0
        }} />
      )}
      <Container fluid className="suchbilder-game-container" style={{
        position: 'relative',
        padding: windowWidth < 1400 ? '10px' : '15px',
        zIndex: 1
      }}>
        {/* Sprechhelden Hero Flying - Top Left Corner (hidden in tablet portrait mode) */}
        {!isTabletPortraitMode && (
          <img
            src={sprechheldenHeldFlying}
            alt="Sprechhelden Held Flying"
            style={{
              position: 'absolute',
              top: '-8px',
              left: windowWidth < 900 ? '15%' :
                    windowWidth < 1200 ? '15%' :
                    windowWidth < 1400 ? '16%' :
                    windowWidth < 1600 ? '15%' :
                    windowWidth < 1800 ? '15%' : '2%',
              width: '110px',
              height: 'auto',
              zIndex: 1000,
              transition: 'left 0.3s ease'
            }}
          />
        )}
        <div className="text-center" style={{ height: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {feedback ? (
            <h2 style={{ 
              color: feedback.type === 'success' ? 'green' : 
                     feedback.type === 'info' ? '#0dcaf0' : 'red', 
              margin: 0,
              fontSize: '19.2px'
            }}>
              {feedback.message}
            </h2>
          ) : gameComplete ? (
            <h2 style={{ margin: 0, color: 'green' }}>Glückwunsch! Du hast 5 Objekte gefunden!</h2>
          ) : selectedObjects.length > 0 && unfoundObjects.length > 0 ? (
            <>
              <h2 style={{
                margin: 0,
                fontWeight: 'bold',
                fontSize: '19.2px',
                color: '#4A4A4A',
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                {selectedPicture && selectedPicture.laut
                  ? `Finde 5 beliebige versteckte Objekte mit den Lauten: ${
                      Array.isArray(selectedPicture.laut)
                        ? selectedPicture.laut.map(l => `"${l.toUpperCase()}"`).join(', ')
                        : `"${selectedPicture.laut.toUpperCase()}"`
                    }`
                  : 'Finde 5 beliebige versteckte Objekte!'
                }
              </h2>
              {gameMode === 'two-player' && (
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '12.8px',
                  color: '#7A7A7A',
                  fontWeight: '500'
                }}>
                  Spieler {currentPlayer} ist dran!
                </p>
              )}
            </>
          ) : (
            <h2 style={{ margin: 0 }}>Lädt...</h2>
          )}
        </div>

        {/* Player indicators for two-player mode */}
        {gameMode === 'two-player' && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '100px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: currentPlayer === 1 ? '#FF6B6B' : '#999',
                transition: 'color 0.3s ease'
              }}>
                Spieler 1
              </div>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: currentPlayer === 1 ? '#FF6B6B' : '#999',
                border: '2px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                fontWeight: 'bold',
                color: 'white',
                boxShadow: currentPlayer === 1 
                  ? '0 6px 20px rgba(255, 107, 107, 0.4)' 
                  : '0 3px 8px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                animation: currentPlayer === 1 ? 'pulse 1s infinite' : 'none'
              }}>
                {playerScores.player1}
              </div>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: currentPlayer === 2 ? '#4ECDC4' : '#999',
                transition: 'color 0.3s ease'
              }}>
                Spieler 2
              </div>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: currentPlayer === 2 ? '#4ECDC4' : '#999',
                border: '2px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                fontWeight: 'bold',
                color: 'white',
                boxShadow: currentPlayer === 2 
                  ? '0 6px 20px rgba(78, 205, 196, 0.4)' 
                  : '0 3px 8px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                animation: currentPlayer === 2 ? 'pulseCyan 1s infinite' : 'none'
              }}>
                {playerScores.player2}
              </div>
            </div>
          </div>
        )}

        {/* Fortschrittsbalken */}
        {selectedObjects.length > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            marginRight: '70px'
          }}>
            <div style={{ 
              maxWidth: windowWidth < 900 ? '20%' :
                        windowWidth < 1200 ? '70%' : 
                        windowWidth < 1400 ? '60%' : 
                        windowWidth < 1600 ? '70%' : 
                        windowWidth < 1800 ? '75%' : '100%',
              padding: '0 20px 5px',
              transition: 'max-width 0.3s ease',
              width: '100%'
            }}>
              <ProgressBar 
                now={(foundObjects.size / 5) * 100} 
                variant="success"
                animated={!gameComplete}
                style={{ height: '25px' }}
                label={`${foundObjects.size} / 5`}
              />
            </div>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: windowWidth < 1400 ? '10px' : '20px',
          flexWrap: windowWidth < 900 ? 'wrap' : 'nowrap'
        }}>
          <div style={{ 
            position: 'relative', 
            display: 'inline-block',
            maxWidth: windowWidth < 900 ? '20%' :
                      windowWidth < 1200 ? '70%' : 
                      windowWidth < 1400 ? '60%' : 
                      windowWidth < 1600 ? '70%' : 
                      windowWidth < 1800 ? '75%' : '100%',
            transition: 'max-width 0.3s ease'
          }}>
            <img 
              src={selectedPicture?.imageUrl || '/findImage.png'} 
              alt="Suchbild" 
              style={{
                width: '100%',
                height: 'auto',
                cursor: gameComplete || !currentObject ? 'default' : 'pointer',
                border: '4px solid white',
                borderRadius: '10px'
              }}
              onClick={handleClick}
            />
            {/* Check marks for found objects */}
            {!gameComplete && foundObjectPositions.map((position, index) => (
              <div
                key={index}
                className="check-mark"
                style={{
                  left: `${position.x}%`,
                  top: `${position.y}%`
                }}
              >
                ✓
              </div>
            ))}
            {/* Glowing overlay for help */}
            {glowingObject !== null && selectedObjects[glowingObject] && (
              <div
                className="object-glow"
                style={{
                  left: `${((selectedObjects[glowingObject].area.minX + selectedObjects[glowingObject].area.maxX) / 2) * 100}%`,
                  top: `${((selectedObjects[glowingObject].area.minY + selectedObjects[glowingObject].area.maxY) / 2) * 100}%`,
                  width: `${(selectedObjects[glowingObject].area.maxX - selectedObjects[glowingObject].area.minX) * 100}%`,
                  height: `${(selectedObjects[glowingObject].area.maxY - selectedObjects[glowingObject].area.minY) * 100}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              />
            )}
            {gameComplete && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 128, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '120px',
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                animation: 'fadeInOverlay 0.8s ease-in-out',
                borderRadius: '10px'
              }}>
                <span style={{
                  animation: 'bounceCheckmark 0.6s ease-out 0.2s both'
                }}>
                  ✓
                </span>
              </div>
            )}
          </div>

          {/* Game controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px'
          }}>
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id="help-tooltip">Hilfe - Objektname zeigen</Tooltip>}
            >
              <Button 
                onClick={showHelp}
                variant="info"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                disabled={gameComplete || unfoundObjects.length === 0}
              >
                <img 
                  src={helpLogo} 
                  alt="Hilfe" 
                  style={{ 
                    width: '40px', 
                    height: '40px',
                    objectFit: 'contain'
                  }} 
                />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id="mute-tooltip">{isMuted ? 'Ton einschalten' : 'Ton ausschalten'}</Tooltip>}
            >
              <Button
                onClick={toggleMute}
                variant={isMuted ? "outline-warning" : "warning"}
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                {isMuted ? <IoVolumeMuteOutline size={24} /> : <Icon name="volume-on" size="lg" />}
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id="restart-tooltip">Neu starten</Tooltip>}
            >
              <Button 
                onClick={resetGame}
                variant="success"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="refresh" size="lg" />
              </Button>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id="change-picture-tooltip">Bild wechseln</Tooltip>}
            >
              <Button 
                onClick={goBackToSelection}
                variant="primary"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="arrow-left" size="lg" />
              </Button>
            </OverlayTrigger>

            {onBackToGames && (
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
              >
                <Button 
                  onClick={onBackToGames}
                  variant="secondary"
                  size="lg"
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <LuGamepad2 size={24} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>

        {/* Word Review Modal */}
        <Modal 
          show={showWordReview} 
          onHide={() => setShowWordReview(false)}
          backdrop="static"
          keyboard={false}
          size="lg" 
          centered
        >
          <Modal.Header style={{ 
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.375rem 0.375rem 0 0'
          }}>
            <Modal.Title style={{ 
              fontWeight: 'bold',
              color: '#4A4A4A'
            }}>
              Wörter wiederholen
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '2rem' }}>
            <p className="text-center mb-4" style={{ 
              fontSize: '16px',
              color: '#7A7A7A',
              marginBottom: '1.5rem'
            }}>
              Super! Du hast 5 Wörter gefunden, wiederhole sie:
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: isTabletPortraitMode ? '12px' : '15px',
              marginBottom: '2rem',
              flexWrap: isTabletPortraitMode ? 'wrap' : 'nowrap'
            }}>
              {foundWords.map((word, index) => (
                <div
                  key={index}
                  onClick={() => playAudio(word.audio)}
                  style={{
                    width: isTabletPortraitMode ? '120px' : '140px',
                    height: isTabletPortraitMode ? '120px' : '140px',
                    borderRadius: '15px',
                    backgroundColor: '#88D8B0',
                    border: '2px solid #88D8B0',
                    boxShadow: '0 4px 12px rgba(136, 216, 176, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px',
                    overflow: 'hidden',
                    flexShrink: isTabletPortraitMode ? 1 : 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#78C8A0';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#88D8B0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {word.image ? (
                    <img
                      src={word.image}
                      alt={word.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '10px'
                      }}
                    />
                  ) : (
                    <span style={{
                      color: '#4A4A4A',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      {word.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
            
            {/* GameWon character at bottom of modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '2rem',
              position: 'relative'
            }}>
              <div style={{
                position: 'relative',
                display: 'inline-block',
                transform: isTabletPortraitMode ? 'translateX(-40px)' : 'none'
              }}>
                <img
                  src={gameWon}
                  alt="Game Won Character"
                  style={{
                    width: '200px',
                    height: 'auto',
                    zIndex: 1,
                    animation: 'float 3s ease-in-out infinite'
                  }}
                />
                
                {/* Speech bubble text positioned relative to character */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    left: isTabletPortraitMode ? '260px' : '270px',
                    transform: 'translateX(-50%)',
                    zIndex: 2,
                    backgroundColor: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '15px',
                    border: '2px solid #333333',
                    fontSize: '14px',
                    color: '#333',
                    textAlign: 'center',
                    minWidth: '200px',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>Fantastisch! Super gemacht!</span>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{
            borderTop: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            borderRadius: '0 0 0.375rem 0.375rem',
            display: 'flex',
            justifyContent: 'center',
            gap: '15px'
          }}>
            <Button
              onClick={() => {
                setShowWordReview(false);
                resetGame();
              }}
              style={{
                minWidth: '150px',
                minHeight: '60px',
                borderRadius: '30px',
                backgroundColor: '#FF6B6B',
                border: '2px solid #FF6B6B',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              Nochmal spielen
            </Button>
            <Button
              onClick={() => {
                setShowWordReview(false);
                goBackToSelection();
              }}
              style={{
                minWidth: '150px',
                minHeight: '60px',
                borderRadius: '30px',
                backgroundColor: '#4ECDC4',
                border: '2px solid #4ECDC4',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              Neues Bild wählen
            </Button>
            <Button
              onClick={playWithDifferentPicture}
              style={{
                minWidth: '150px',
                minHeight: '60px',
                borderRadius: '30px',
                backgroundColor: '#88D8B0',
                border: '2px solid #88D8B0',
                color: '#4A4A4A',
                fontWeight: 'bold',
                fontSize: '16px',
                boxShadow: '0 4px 12px rgba(136, 216, 176, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              Weiterspielen
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Confetti Effect */}
        {gameComplete && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={showConfetti}
            numberOfPieces={200}
            gravity={0.3}
            style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
          />
        )}
      </Container>
    </>
  );
}

export default SuchbilderGame;